/**
 * Component: Cart add-on script
 * ------------------------------------------------------------------------------
 * Loosely coupled scripts for the Cart add-on functionality.
 *
 * @namespace cartAddOn
 */
import * as cart from '@shopify/theme-cart';
import axios from 'axios';

import cssClasses from '~/helpers/cssClasses';
import {debounce, on} from '~/helpers/utils';
import Modal from '~/components/modal';

/**
 * DOM selectors.
 */
const selectors = {
  panel: '[js-cart="panel"]',
  container: '[js-cart-add-on="container"]',
  checkbox: '[js-cart-add-on="checkbox"]',
  popupTrigger: '[js-cart-add-on="trigger"]',
  label: '[js-cart-add-on="label"]',
  message: '[js-cart-add-on="message"]',
  textarea: '[js-cart-add-on="textarea"]',
  msgCheckbox: '[js-cart-add-on="messageCheckbox"]',
  msgCheckboxLabel: '[js-cart-add-on="messageCheckboxLabel"]',
  maxCharText: '[js-cart-add-on="maxChar"]',
  countCharText: '[js-cart-add-on="countChar"]',
  cancelMsg: '[js-cart-add-on="messageCancel"]',
  previewMsg: '[js-cart-add-on="messagePreview"]',
  confirmMsg: '[js-cart-add-on="messageConfirm"]',
  line: '[js-cart-add-on="line"]',
  modal: '[js-cart-add-on="modal"]',
  modalClose: '[js-cart-add-on="closeModal"]',
  modalCard: '[js-cart-add-on="modalCard"]',
  descShow: '[js-cart-add-on="descShow"]',
  description: '[js-cart-add-on="description"]',
  preview: '[js-cart-add-on="previewContainer"]',
  messageError: '[js-cart-add-on="messageError"]',
  modalMessage: '[js-cart-add-on="previewMessage"]',
  largeMessage: '[js-cart-add-on="largeMessage"]',
  checkoutBtn: '[js-cart-table="submit"]',
};


/**
 * Instance settings.
 */
const settings = {
  id: 'add-on-modal',
};

/**
 * Create a new cart add-on object.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    panel: document.querySelector(selectors.panel),
    modal: document.querySelector(selectors.modal),
    modalClose: document.querySelector(selectors.modalClose),
    modalCard: document.querySelector(selectors.modalCard),
    modalMessage: document.querySelector(selectors.modalMessage),
    previewMsg: document.querySelector(selectors.previewMsg),
    description: document.querySelector(selectors.description),
    preview: document.querySelector(selectors.preview),
  };

  /**
   * DOM add-on selectors.
   */
  const addOnSelectors = {
    container: nodeSelectors.panel.querySelector(selectors.container),
    checkbox: nodeSelectors.panel.querySelector(selectors.checkbox),
    popupTrigger: nodeSelectors.panel.querySelector(selectors.popupTrigger),
    label: nodeSelectors.panel.querySelector(selectors.label),
    message: nodeSelectors.panel.querySelector(selectors.message),
    textarea: nodeSelectors.panel.querySelector(selectors.textarea),
    msgCheckbox: nodeSelectors.panel.querySelector(selectors.msgCheckbox),
    msgCheckboxLabel: nodeSelectors.panel.querySelector(selectors.msgCheckboxLabel),
    maxCharText: nodeSelectors.panel.querySelector(selectors.maxCharText),
    countCharText: nodeSelectors.panel.querySelector(selectors.countCharText),
    cancelMsg: nodeSelectors.panel.querySelector(selectors.cancelMsg),
    confirmMsg: nodeSelectors.panel.querySelector(selectors.confirmMsg),
    line: nodeSelectors.panel.querySelector(selectors.line),
    descShow: nodeSelectors.panel.querySelector(selectors.descShow),
    messageError: nodeSelectors.panel.querySelector(selectors.messageError),
    largeMessage: nodeSelectors.panel.querySelector(selectors.largeMessage),
    checkoutBtn: nodeSelectors.panel.querySelector(selectors.checkoutBtn),
  };

  /**
   * Instance globals.
   */
  const modal = Modal({
    id: settings.id,
  });

  /**
   * Message Cache
   */
  let messageCache = '';

  /**
   * Initialise component.
   */
  function init() {
    if (!nodeSelectors.panel) {
      return;
    }

    modal.init();
    setEventBusListeners();
    setEventListeners();
  }

  /**
   * Set eventBus listeners.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen('CartTable:rendered', (response) => setInitState(response));
    Frame.EventBus.listen(`Modal:Show:${settings.id}`, () => onModalShowCallback());
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    on('click', addOnSelectors.descShow, (event) => addOnDescShow(event));
    on('click', nodeSelectors.modalClose, () => handleModalCloseEvent());

    if (theme.features.cartAddOnMessage) {
      on('click', nodeSelectors.previewMsg, (event) => addOnPreview(event));
      on('click', addOnSelectors.msgCheckboxLabel, () => handleMessageToggle());
      on('click', addOnSelectors.cancelMsg, () => handleMsgCancel());
      on('click', addOnSelectors.confirmMsg, handleMessageSave);
      on('keyup', addOnSelectors.message, debounce(handleMessageUpdate, 250));
      on('blur', addOnSelectors.textarea, debounce(checkMessageError, 250));
      on('change', addOnSelectors.msgCheckbox, () => handleMessageCheckboxToggle());
    }

    on('change', addOnSelectors.checkbox, (event) => handleAddOnToggle(event));
  }

  /**
   * Set initial state of add-on form.
   * @param {Object} data - Cart.js data.
   */
  function setInitState(data) {
    if (isAddOnInCart(data)) {
      checkOnlyLargeItem(data);
      return;
    }

    resetActiveState();

    // here
  }

  /**
   * Check if add-on product is already in cart.
   * @param {Object} data - Cart.js data.
   */
  function isAddOnInCart(data) {
    if (isOnlyItemInCart(data)) {
      return false;
    }

    return data.items.some((item) => {
      return (item.product_type.toLowerCase() === 'add-on');
    });
  }

  /**
   * Check if the only item in cart is an add-on.
   * @param {Object} data - Cart.js data.
   * @returns {Boolean}
   */
  function isOnlyItemInCart(data) {
    const totalNumberOfItems = data.items.reduce((total, item) => {
      return (item.product_type.toLowerCase() === 'add-on') ? 0 : total + item.quantity;
    }, 0);

    if (totalNumberOfItems === 0 && data.item_count > 0) {
      Frame.EventBus.emit('AjaxCart:clearCart');
    }

    return (totalNumberOfItems === 0);
  }

  /**
   * Set add-on state to active.
   */
  function setActiveState(largeMessage) {
    addOnSelectors.container.setAttribute('data-active', true);
    addOnSelectors.checkbox.checked = true;
    addOnSelectors.line.classList.remove(cssClasses.hidden);

    if (theme.features.cartAddOnMessage) {
      addOnSelectors.message.classList.add(cssClasses.active);
    }

    if (addOnSelectors.largeMessage) {
      addOnSelectors.largeMessage.classList.add(cssClasses.active);

      if (largeMessage === 0) {
        addOnSelectors.largeMessage.classList.remove(cssClasses.active);
      }
    }

    checkMessageActive();

    window.dataLayer.push({
      event: 'GAevent',
      eventID: '19',
      eventCategory: 'cart',
      eventAction: 'cart_interaction',
      eventLabel: 'add_gift_wrap',
    });
  }

  /**
   * Check if only large items in cart.
   * @param {Object} response - cart response data.
   */
  function checkOnlyLargeItem(response) {
    let itemCount = response.item_count;
    const hasAddOn = response.items.find((item) => {
      return item.product_type.toLowerCase() === 'add-on';
    });

    const largeItems = response.items.filter((item) => {
      return (item.properties !== null) && (item.properties._large_item !== undefined);
    });

    if (hasAddOn) { itemCount--; }

    if (largeItems.length < itemCount) {
      setActiveState(largeItems.length);
      return;
    }

    addOnSelectors.checkbox.removeAttribute('checked');
    addOnSelectors.checkbox.setAttribute('disabled', 'disabled');
    addOnSelectors.largeMessage.classList.add('color--brand');

    addOnSelectors.largeMessage.innerHTML = `
      <span>${theme.icons.information}</span>
      <span>${theme.strings.cart.add_on.large_item_warning_all}</span>
    `;

    addOnSelectors.container.setAttribute('data-active', false);
    addOnSelectors.checkbox.checked = false;
    findAndRemoveAddOn();
  }

  /**
   * Handle message state.
   * @param {String} action - make active or add message.
   * @param {String} note - add-on message.
   */
  function handleMessageState(action, note) {
    addOnSelectors.textarea.removeAttribute('disabled');
    addOnSelectors.textarea.parentNode.classList.remove(cssClasses.disabled);
    addOnSelectors.maxCharText.classList.add(cssClasses.active);
    addOnSelectors.cancelMsg.removeAttribute('disabled');

    if (action === 'add') {
      addOnSelectors.textarea.focus();
      addOnSelectors.checkoutBtn.setAttribute('disabled', 'disabled');

      return;
    }

    addOnSelectors.msgCheckboxLabel.parentNode.querySelector('input').checked = true;
    addOnSelectors.textarea.value = note;
    nodeSelectors.modalMessage.innerHTML = note;
    messageCache = note;
    nodeSelectors.previewMsg.removeAttribute('disabled');
  }

  /**
   * Reset add-on state to inactive.
   */
  function resetActiveState() {
    addOnSelectors.container.setAttribute('data-active', false);
    addOnSelectors.checkbox.checked = false;

    if (theme.features.cartAddOnMessage) {
      addOnSelectors.message.classList.remove(cssClasses.active);
      addOnSelectors.textarea.value = '';
    }
  }

  /**
   * Handle add on toggle events.
   * - Add/Remove existing add-on product in cart.
   * - Show/hide message if enabled in theme settings.
   */
  function handleAddOnToggle(event) {
    if (event.target.tagName === 'BUTTON') { return; }

    if (addOnSelectors.largeMessage) {
      addOnSelectors.largeMessage.classList.add(cssClasses.active);

      if (addOnSelectors.largeMessage.dataset.all === 'true') {
        addOnSelectors.checkbox.removeAttribute('checked');
        addOnSelectors.label.classList.add(cssClasses.disabled);
        return;
      }
    }

    Frame.EventBus.emit('CartTable:updating');

    if (addOnSelectors.checkbox.checked === true) {
      addAddOn();
      return;
    }

    findAndRemoveAddOn();
    addOnSelectors.msgCheckboxLabel.parentNode.querySelector('input').checked = false;
  }

  /**
   * Handle message toggle.
   */
  function handleMessageToggle() {
    if (messageIncluded()) {
      removeMessage();
      return;
    }

    handleMessageState('add');
  }

  /**
   * Handle message checkbox toggle.
   */
  function handleMessageCheckboxToggle() {
    if (messageIncluded()) {
      handleMessageState('add');
      return;
    }

    removeMessage();
    updateAddOnMessage('');
  }

  /**
   * Handle message Cancel.
   */
  async function handleMsgCancel() {
    addOnSelectors.textarea.value = '';
    nodeSelectors.modalMessage.innerHTML = '';
    await cart.updateNote('');
    await handleMessageSave();
  }

  /**
   * Remove gift message.
   */
  function removeMessage() {
    addOnSelectors.textarea.setAttribute('disabled', 'disabled');
    addOnSelectors.textarea.parentNode.classList.add(cssClasses.disabled);
    addOnSelectors.textarea.parentNode.classList.remove(cssClasses.haserror);
    addOnSelectors.textarea.value = '';
    nodeSelectors.modalMessage.innerHTML = '';
    cart.clearNote('');
    addOnSelectors.maxCharText.classList.remove(cssClasses.active);
    addOnSelectors.countCharText.classList.remove(cssClasses.active);
    addOnSelectors.cancelMsg.setAttribute('disabled', 'disabled');
    nodeSelectors.previewMsg.setAttribute('disabled', 'disabled');
    addOnSelectors.confirmMsg.setAttribute('disabled', 'disabled');
    addOnSelectors.messageError.classList.remove(cssClasses.active);
    addOnSelectors.checkoutBtn.removeAttribute('disabled');
  }

  /**
   *  Check if addon message is included.
   */
  function messageIncluded() {
    return addOnSelectors.msgCheckbox.checked;
  }

  /**
   * Is texarea message active.
   */
  function checkMessageActive() {
    cart.getNote()
      .then((response) => {
        const note = response;

        if (note.length > 0) {
          handleMessageState('active', note);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }

  async function updateAddOnMessage(message) { // Setting it to empty string will remove message
    const messageKey = addOnSelectors.container.dataset.messageKey;

    const addOnToUpdate = await getAddOnFromCart();
    if(!addOnToUpdate) return;

    const properties = addOnToUpdate.properties;
    properties[String(messageKey)] = message; // Adding new message, NB. messagekey must not change!


    cart.updateItem(addOnToUpdate.key, {qty: 1, properties: properties })
      .then(() => {
        Frame.EventBus.emit('AjaxCart:addOnItemAdded');
      })
      .catch((error) => {
        throw new Error('Could not update item in cart.', error);
      });
  }

  /**
   * Add add-on product from theme settings to cart with validation.
   */
  async function addAddOn() {
    const id = Number(addOnSelectors.container.dataset.variantId);

     await cart.addItem(id, {qty: 1})
      .then(() => {
        Frame.EventBus.emit('AjaxCart:addOnItemAdded');
        addOnSelectors.line.classList.remove(cssClasses.hidden);
      })
      .catch((error) => {
        throw new Error('Could not add item to cart. Check product inventory.', error);
      });
  }

  async function getAddOnFromCart(callback) {
    const addOn = await axios.get(`/cart.js?no-cache=${new Date().getTime()}`)
    .then((response) => {
      const addOn = response.data.items.find((item) => {
        return item.product_type.toLowerCase() === 'add-on';
      });

      if(callback && typeof callback === 'function') {
        callback(addOn);
      }
      return addOn;
    })
    .catch((error) => {
      throw new Error('Could not find ', error);
    });

    return addOn;
  }

  /**
   * Find and remove add-on product from cart.
   */
  async function findAndRemoveAddOn() {
    await getAddOnFromCart(async function(addOn){
       await removeAddOn(addOn);
      removeMessage();
      updateAddOnMessage('');
    });
  }

  /**
   * Find and remove add-on product from cart.
   * @param {Object} product - Add-on product object from cart.js.
   */
  async function removeAddOn(product) {
    await cart.removeItem(product.key)
      .then(() => {
        resetActiveState();
        cart.clearNote();
        Frame.EventBus.emit('AjaxCart:addOnItemRemoved');
        addOnSelectors.line.classList.add(cssClasses.hidden);
      })
      .catch((error) => {
        throw new Error('Could not remove add-on product from cart', error);
      });
  }

  /**
   * Handle text inputs on the add-on message textarea element.
   */
  function handleMessageUpdate(event) {
    // const id = Number(addOnSelectors.container.dataset.variantId);

    if (event.target !== addOnSelectors.textarea) { return; }

    const note = addOnSelectors.textarea.value;
    const message = removeEmoji(note);

    addOnSelectors.textarea.value = message;
    addOnSelectors.textarea.innerHTML = message;
    nodeSelectors.modalMessage.innerHTML = message;

    handleMessageCount(message);
  }

  async function handleMessageSave() {
    const note = addOnSelectors.textarea.value;
    const message = removeEmoji(note);
    const messageKey = addOnSelectors.container.dataset.messageKey;
    const addOnToUpdate = await getAddOnFromCart();

    cart.updateNote(message);
    const properties = {};
    properties[String(messageKey)] = message;

    const cartUpdate = await cart.updateItem(addOnToUpdate.key, {
      properties: properties,
    });

    if (cartUpdate && cartUpdate.items && cartUpdate.items.length) {
      const gwItem = cartUpdate.items.find(item => item.product_type === "add-on");

      if (gwItem) {
        messageCache = gwItem.properties[String(messageKey)];
        handleMessageCount(gwItem.properties[String(messageKey)]);
      }
    }
  }



  /**
   * Check textarea error.
   */
  function checkMessageError(event) {
    on('blur', event.target, () => {
      const note = addOnSelectors.textarea.value;
      const message = removeEmoji(note);

      handleMessageCount(message);
    });
  }

  /**
   * Handle message count.
   * @param {String} message.
   */
  function handleMessageCount(message) {
    if (addOnSelectors.textarea.hasAttribute('disabled')) { return; }

    const count = message.length;
    const charCheck = ~~theme.features.cartAddOnMessageMax - count;

    if (count > 0) {
      addOnSelectors.countCharText.innerHTML = theme.strings.addOnCharCount.replace('%count%', charCheck);

      addOnSelectors.maxCharText.classList.remove(cssClasses.active);
      addOnSelectors.countCharText.classList.add(cssClasses.active);
      addOnSelectors.messageError.classList.remove(cssClasses.active);
      addOnSelectors.textarea.parentNode.classList.remove(cssClasses.haserror);

      if (message === messageCache) {
        addOnSelectors.confirmMsg.setAttribute('disabled', 'disabled');
        nodeSelectors.previewMsg.removeAttribute('disabled');
        addOnSelectors.checkoutBtn.removeAttribute('disabled');
      } else {
        addOnSelectors.confirmMsg.removeAttribute('disabled');
        nodeSelectors.previewMsg.removeAttribute('disabled');
        addOnSelectors.checkoutBtn.setAttribute('disabled', 'disabled');
      }

      return;
    } else {
      nodeSelectors.confirmMsg.setAttribute('disabled', 'disabled');
    }

    addOnSelectors.maxCharText.classList.add(cssClasses.active);
    addOnSelectors.countCharText.classList.remove(cssClasses.active);
    addOnSelectors.messageError.classList.add(cssClasses.active);
    addOnSelectors.textarea.parentNode.classList.add(cssClasses.haserror);
    addOnSelectors.checkoutBtn.setAttribute('disabled', 'disabled');
  }

  /**
   * Show add-on description.
   * @param {Event} event - click event.
   */
  function addOnDescShow(event) {
    event.preventDefault();

    nodeSelectors.description.classList.remove(cssClasses.hidden);
    nodeSelectors.preview.classList.add(cssClasses.hidden);
    handleModalShowEvent();
  }

  /**
   * Show add-on preview.
   * @param {Event} event - click event.
   */
  function addOnPreview(event) {
    event.preventDefault();

    nodeSelectors.description.classList.add(cssClasses.hidden);
    nodeSelectors.preview.classList.remove(cssClasses.hidden);
    handleModalShowEvent();
  }

  /**
   * Handle modal show.
   */
  function handleModalShowEvent() {
    modal.show(settings.id);
  }

  /**
   * Handle modal close.
   */
  function handleModalCloseEvent() {
    modal.close(settings.id);
  }

  /**
   * Disallow emojis in textarea.
   * @param {String} text - add-on text.
   */
  function removeEmoji(text) {
    const regex = /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;

    return text.replace(regex, '');
  }

  function onModalShowCallback() {
    on('click', nodeSelectors.modal, (event) => {

      window.setTimeout(() => {
        if (nodeSelectors.modalCard.contains(event.target)) { return; }
        handleModalCloseEvent();
      }, 300);
    });
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
